import { useState } from "react";
import { toast } from "sonner";
import { Instagram, Link2, Check, Share2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.5.3-.5c.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
  </svg>
);

export const ShareBar = ({ slug, title, image }) => {
  const { lang, copy } = useLanguage();
  const s = copy.share;
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/api/share/${slug}?lang=${lang}`;
  const text = `${title} · Gusto y Ciencia`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(s.copied);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(shareUrl);
    }
  };

  const instagram = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: text, text, url: shareUrl });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    await copyLink();
    toast(s.igHint, { description: image ? s.igImageHint : undefined, duration: 6000 });
  };

  const btn = "inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors duration-300";

  return (
    <div className="mt-12 rounded-3xl bg-sand p-6 sm:p-7" data-testid="share-bar">
      <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-sage mb-4"><Share2 size={14} /> {s.title}</p>
      <div className="flex flex-wrap gap-3">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${text}\n${shareUrl}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp"
          className={`${btn} border-[#25D366]/40 bg-white text-ink hover:bg-[#25D366] hover:text-white hover:border-[#25D366]`}
        >
          <WhatsAppIcon /> WhatsApp
        </a>
        <button type="button" onClick={instagram} data-testid="share-instagram" className={`${btn} border-clay/40 bg-white text-ink hover:bg-clay hover:text-white hover:border-clay`}>
          <Instagram size={16} /> Instagram
        </button>
        <button type="button" onClick={copyLink} data-testid="share-copy" className={`${btn} border-border bg-white text-ink hover:border-pine hover:text-pine`}>
          {copied ? <Check size={16} /> : <Link2 size={16} />} {copied ? s.copied : s.copy}
        </button>
      </div>
      <p className="mt-4 text-xs text-muted leading-relaxed">{s.preview}</p>
    </div>
  );
};
